<script lang="ts">
  import { AlertTriangle, CheckCircle2, HelpCircle, Scale, XCircle } from 'lucide-svelte';
  import type { ParityStatus } from '@rhdashboard-vnext/shared';
  import { Button } from '$lib/components/ui/button';
  import ParityCheckModal from './ParityCheckModal.svelte';
  import WarmupParityModal from './WarmupParityModal.svelte';

  // ============================================================
  // <ParityWidget>  -  Fase 6.
  //
  // Compone el badge WP:* + el boton "Run Parity" y maneja apertura de
  // los dos modales (ParityCheck + WarmupParity). Usable desde SessionCard,
  // SessionsTable y ChartAnalysis row.
  //
  // Props:
  //   sessionId    -  required, para los fetches.
  //   warmupStatus -  status del badge WP:*. Si null, el badge no se muestra
  //                   (la session aun no tiene warmupParityResult en Atlas).
  //   compact      -  oculta el texto del boton (solo icono) para fits chicos.
  // ============================================================

  interface Props {
    sessionId: string;
    warmupStatus?: ParityStatus | string | null;
    compact?: boolean;
  }

  let { sessionId, warmupStatus = null, compact = false }: Props = $props();

  let checkOpen = $state(false);
  let warmupOpen = $state(false);
  // Flag para que WarmupParityModal sepa si tiene un "back to parity check"
  // disponible. true cuando entramos via boton status del Parity Check Modal.
  let warmupCameFromCheck = $state(false);

  // Cuando el ParityCheck modal pide abrir warmup, cerramos el check pero
  // marcamos warmupCameFromCheck para habilitar "Back". El state interno del
  // check (result, alertInterval, etc) se preserva porque NO desmontamos —
  // el bind:open hace toggle visual sin destruir el componente. Cuando el
  // user hace Back, simplemente abrimos checkOpen=true otra vez.
  function openWarmupFromCheck(): void {
    checkOpen = false;
    warmupCameFromCheck = true;
    warmupOpen = true;
  }

  function backToCheckFromWarmup(): void {
    warmupOpen = false;
    warmupCameFromCheck = false;
    checkOpen = true;
  }

  function onWarmupClose(): void {
    warmupOpen = false;
    warmupCameFromCheck = false;
  }

  function statusInfo(s: ParityStatus | string | null | undefined) {
    if (s === 'OK')
      return { cls: 'border-success/40 bg-success/15 text-success', icon: CheckCircle2, label: 'OK' };
    if (s === 'DRIFT')
      return { cls: 'border-warning/40 bg-warning/15 text-warning', icon: AlertTriangle, label: 'DRIFT' };
    if (s === 'CRITICAL')
      return {
        cls: 'border-destructive/40 bg-destructive/15 text-destructive',
        icon: XCircle,
        label: 'CRITICAL',
      };
    return {
      cls: 'border-muted/40 bg-muted/15 text-muted-foreground',
      icon: HelpCircle,
      label: String(s ?? '—'),
    };
  }

  let si = $derived(statusInfo(warmupStatus));
</script>

<div class="inline-flex items-center gap-1.5">
  {#if warmupStatus}
    <button
      type="button"
      class={[
        'inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider transition-colors hover:brightness-110',
        si.cls,
      ].join(' ')}
      onclick={(e) => {
        e.stopPropagation();
        warmupOpen = true;
      }}
      title="Click to open Warmup Parity detail"
    >
      <si.icon class="size-3" />
      WP:{si.label}
    </button>
  {/if}

  <Button
    variant="ghost"
    size="sm"
    class="h-7 gap-1 px-2"
    onclick={(e) => {
      e.stopPropagation();
      checkOpen = true;
    }}
    title="Run Parity Check (~1min)"
  >
    <Scale class="size-3.5" />
    {#if !compact}
      <span class="text-xs">Parity</span>
    {/if}
  </Button>
</div>

<ParityCheckModal
  bind:open={checkOpen}
  {sessionId}
  onClose={() => {
    checkOpen = false;
  }}
  onOpenWarmup={openWarmupFromCheck}
/>

<WarmupParityModal
  bind:open={warmupOpen}
  {sessionId}
  onClose={onWarmupClose}
  onBack={warmupCameFromCheck ? backToCheckFromWarmup : undefined}
/>
